/**
 * voice-v8 — Werkzeugkarten: Aufgaben: Ticket, Hintergrund-Aufgaben (Delegieren, Liste), Server-Aufgabe.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { absatz, baueKarte, fehlerBlock, kartenTitel, wertZeile } from "../karte.js";
import { gesteuert } from "../ruckel-schalter.js";
import { menschlicherFehler } from "../fehler-worte.js";
import { trimText, unwrapResult } from "./hilfe.js";

// Pre.793 (#68): ticket_create — saubere Karte statt roher Key-Value-Liste.
// Badges reusen die bestehenden .tickets-badge-Klassen der Ticket-Liste.
interface TicketResultShape {
  ok?: boolean;
  ticket_id?: number;
  status?: string;
  severity?: string;
  message?: string;
  error?: string;
  title?: string;
  description?: string;
}

const TICKET_STATUS_LABELS: Record<string, string> = {
  open: "offen",
  in_progress: "in Arbeit",
  resolved: "erledigt",
};

export function buildTicketCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as TicketResultShape;
  const el = baueKarte("ticket");
  el.appendChild(kartenTitel("Aufgabe"));
  if (r.ok === false) {
    el.appendChild(
      fehlerBlock(
        menschlicherFehler(
          r as unknown as Record<string, unknown>,
          "Die Aufgabe konnte gerade nicht angelegt werden.",
        ),
      ),
    );
    return el;
  }
  const title = document.createElement("div");
  title.className = "tool-ticket-title";
  // 2026-07-03 (Martin, #220): echten Ticket-Titel zeigen (nicht nur „#N
  // angelegt"), damit klar ist, WORUM es geht.
  // 15.10.2026: der Titel ist eine Zeile — auch wenn er aus `message` kommt (Karten-Größe).
  title.textContent = trimText(
    (r.title?.trim()) ||
    r.message ||
    (r.ticket_id ? `Ticket #${r.ticket_id} angelegt` : "Ticket angelegt"),
    120,
  );
  el.appendChild(title);
  if (r.description?.trim()) {
    const desc = document.createElement("div");
    desc.className = "tool-ticket-desc";
    desc.textContent =
      r.description.trim().length > 200
        ? r.description.trim().slice(0, 200) + "…"
        : r.description.trim();
    el.appendChild(desc);
  }

  const badges = document.createElement("div");
  badges.className = "tool-ticket-badges";
  const addBadge = (cls: string, text: string, status?: string): void => {
    const b = document.createElement("span");
    b.className = cls;
    if (status) b.dataset.status = status;
    b.textContent = text;
    badges.appendChild(b);
  };
  if (typeof r.ticket_id === "number") {
    addBadge("tickets-badge", `#${r.ticket_id}`);
  }
  if (r.status) {
    addBadge(
      "tickets-badge tickets-badge-status",
      TICKET_STATUS_LABELS[r.status] ?? r.status,
      r.status,
    );
  }
  if (r.severity) {
    addBadge(`tickets-badge tickets-badge-${r.severity}`, r.severity);
  }
  if (badges.childElementCount > 0) el.appendChild(badges);
  return el;
}

// Pre.360: Flight-Search-Card (Duffel-API). Strukturierte Top-N-Treffer
// mit Airline, Preis, Dauer, Stops, Times — statt rohem JSON.
// Pre.367: Live-Translate-Card. Klarer Layout-Aufbau:
// Header "Übersetzung · DE → EN", dann Original-Text gedimmt, dann
// die Übersetzung in groß und kursiv.
// Pre.397: task_delegate-Card — vorher generic. Zeigt Status mit Pulse,
// Modell, Projekt, Hint dass User im Tasks-Tab den Live-Status sieht.
interface TaskDelegateResult {
  ok?: boolean;
  task_id?: string;
  status?: string;
  project?: string | null;
  model?: string;
  executor?: string;
  message?: string;
  error?: string;
}

export function buildTaskDelegateCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as unknown as TaskDelegateResult;
  const el = baueKarte("task-delegate");
  if (r.ok === false) {
    el.appendChild(kartenTitel("Task delegieren — Fehler"));
        el.appendChild(absatz(String(r.error ?? "Unbekannter Fehler"), "warn"));
    return el;
  }
  const isDesktop = r.executor === "desktop";
  el.appendChild(
    kartenTitel(
      isDesktop ? "Task läuft auf deinem Mac" : "Task läuft im Hintergrund",
    ),
  );

  const meta = document.createElement("div");
  meta.className = "tool-task-meta";
  if (r.project) {
    const pill = document.createElement("span");
    pill.className = "tool-task-pill";
    pill.textContent = r.project;
    meta.appendChild(pill);
  }
  if (r.model) {
    const m = document.createElement("span");
    m.className = "tool-task-model";
    m.textContent = r.model;
    meta.appendChild(m);
  }
  const status = document.createElement("span");
  status.className = "tool-task-status";
  status.textContent = "● läuft";
  meta.appendChild(status);
  el.appendChild(meta);

  const hint = document.createElement("p");
  hint.className = "tool-task-hint";
  hint.textContent = isDesktop
    ? "Vespera Desktop führt aus. Push-Benachrichtigung kommt wenn fertig."
    : "Topbar-Pulse oben zeigt den Status. Push-Benachrichtigung wenn fertig.";
  el.appendChild(hint);

  // 2026-07-14 (Martin, "steht noch 'laeuft', obwohl fertig"): die Karte
  // war STATISCH — sie pollt jetzt den echten Task-Status und dreht auf
  // fertig/fehlgeschlagen (wirkt auch auf alte Karten beim Verlauf-Reload).
  if (r.task_id) {
    const taskId = r.task_id;
    const applyDone = (st: string): void => {
      status.textContent = st === "done" ? "✓ fertig" : `✗ ${st}`;
      status.dataset.state = st === "done" ? "done" : "failed";
      hint.textContent =
        st === "done"
          ? "Fertig — Ergebnis steht in der Meldung/Antwort."
          : "Beendet.";
    };
    const check = async (): Promise<boolean> => {
      try {
        const res = await fetch("/api/background-tasks", {
          credentials: "same-origin",
        });
        if (!res.ok) return false;
        const d = (await res.json()) as {
          tasks?: Array<{ id?: string; status?: string }>;
        };
        const t = (d.tasks ?? []).find((x) => x.id === taskId);
        if (!t) {
          // Nicht mehr in der Liste (alt/aufgeraeumt) -> sicher beendet.
          applyDone("done");
          return true;
        }
        if (t.status && t.status !== "running") {
          applyDone(t.status);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };
    void check(); // sofort (Verlauf-Reload alter Karten)
    let ticks = 0;
    const iv = window.setInterval(gesteuert("live", () => {
      ticks += 1;
      if (!el.isConnected || ticks > 450) {
        window.clearInterval(iv);
        return;
      }
      void check().then((done) => {
        if (done) window.clearInterval(iv);
      });
    }, true), 8000);
  }

  return el;
}

// Pre.397: task_list-Card — strukturierte Übersicht statt JSON-Dump.
interface TaskListResult {
  ok?: boolean;
  total?: number;
  filtered?: number;
  running?: number;
  tasks?: Array<{
    id?: string;
    status?: string;
    project?: string | null;
    model?: string;
    prompt_preview?: string;
    elapsed_sec?: number;
    output_preview?: string | null;
    error?: string | null;
  }>;
}

export function buildTaskListCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as unknown as TaskListResult;
  const el = baueKarte("task-list");
  el.appendChild(
    kartenTitel(`Hintergrund-Tasks · ${r.filtered ?? 0} von ${r.total ?? 0}`),
  );

  const stats = document.createElement("div");
  stats.className = "tool-task-stats";
  const running = r.running ?? 0;
  const done = (r.filtered ?? 0) - running;
  if (running > 0) {
    const s = document.createElement("span");
    s.className = "tool-task-stat tool-task-stat--running";
    s.textContent = `● ${running} laufen`;
    stats.appendChild(s);
  }
  if (done > 0) {
    const s = document.createElement("span");
    s.className = "tool-task-stat tool-task-stat--done";
    s.textContent = `${done} fertig`;
    stats.appendChild(s);
  }
  if (running === 0 && done === 0) {
    const s = document.createElement("span");
    s.className = "tool-task-stat";
    s.textContent = "Keine Tasks";
    stats.appendChild(s);
  }
  el.appendChild(stats);

  const tasks = Array.isArray(r.tasks) ? r.tasks : [];
  if (tasks.length > 0) {
    const list = document.createElement("ul");
    list.className = "tool-task-list";
    for (const t of tasks.slice(0, 5)) {
      const li = document.createElement("li");
      li.className = `tool-task-row tool-task-row--${t.status ?? "unknown"}`;
      const top = document.createElement("div");
      top.className = "tool-task-row-top";
      const stat = document.createElement("span");
      stat.className = "tool-task-row-status";
      // 2026-08-26: Der dritte Fall stand als "✕" da — neben zwei WOERTERN
      // ("läuft", "fertig"). Ein Kreuz ist hier kein Zeichen, sondern ein
      // fehlendes Wort: es sagt nicht, ob abgebrochen, fehlgeschlagen oder
      // nie gestartet. Der Zustand heisst, was er ist.
      stat.textContent =
        t.status === "running"
          ? "● läuft"
          : t.status === "done"
            ? "fertig"
            : t.status === "cancelled"
              ? "abgebrochen"
              : "fehlgeschlagen";
      top.appendChild(stat);
      if (t.project) {
        const p = document.createElement("span");
        p.className = "tool-task-row-project";
        p.textContent = t.project;
        top.appendChild(p);
      }
      const time = document.createElement("span");
      time.className = "tool-task-row-time";
      time.textContent = `${t.elapsed_sec ?? 0}s`;
      top.appendChild(time);
      li.appendChild(top);
      if (t.prompt_preview) {
        const p = document.createElement("p");
        p.className = "tool-task-row-prompt";
        p.textContent = t.prompt_preview;
        li.appendChild(p);
      }
      list.appendChild(li);
    }
    el.appendChild(list);
  }
  return el;
}

// Pre.359: claude_task_server-Card — vorher fiel das in den Generic-Fallback
// mit rohem JSON. Jetzt strukturierte Anzeige mit Model, Output-Preview,
// Elapsed-Time, Session-Info.
export function buildClaudeTaskCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("claude-task");
  const isError = r.ok === false;
  el.appendChild(kartenTitel(isError ? "Claude CLI — Fehler" : "Claude CLI"));
  if (isError) {
    const errMsg = typeof r.error === "string" ? r.error : "Unbekannter Fehler";
    el.appendChild(absatz(errMsg.slice(0, 600), "warn"));
    return el;
  }
  if (typeof r.model === "string") {
    wertZeile(el, "Modell", String(r.model));
  }
  if (typeof r.project === "string" && r.project) {
    wertZeile(el, "Projekt", String(r.project));
  }
  if (r.session_resumed) {
    wertZeile(el, "Session", "fortgesetzt (--resume)");
  } else if (r.session_new) {
    wertZeile(el, "Session", "neu angelegt");
  }
  if (typeof r.elapsed_ms === "number") {
    const s = Math.round(r.elapsed_ms / 1000);
    wertZeile(
      el,
      "Dauer",
      s < 60 ? `${s}s` : `${Math.floor(s / 60)}min ${s % 60}s`,
    );
  }
  const output = typeof r.output === "string" ? r.output : "";
  if (output.trim().length > 0) {
    // 15.10.2026: ein Anriss (400) — Vesperas Antwort trägt den Inhalt, die Karte bestätigt (Karten-Größe).
    el.appendChild(absatz(trimText(output, 400), "result"));
  }
  return el;
}
